import React, { useState } from 'react';
import {
  Phone,
  WhatsappLogo,
  EnvelopeSimple,
  MapPin,
  Clock,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import FormPrivacyNotice from '../components/FormPrivacyNotice';
import { submitLead, normalizePhone } from '@/lib/submitLead';

const PHONE_DISPLAY = '+91 88617 12345';
const PHONE_TEL = 'tel:+918861712345';
const WHATSAPP_URL = 'https://wa.me/918861712345';
const EMAIL = 'shekhar@petcart.com';
const ADDRESS =
  'Survey No 136/18, V Kallhalli, Sarjapura Main Road, Anekal TQ, Bangalore 562125';

const channels = [
  {
    icon: WhatsappLogo,
    label: 'WhatsApp',
    value: 'Chat with us instantly',
    action: 'Open WhatsApp',
    href: WHATSAPP_URL,
    external: true,
  },
  {
    icon: Phone,
    label: 'Call us',
    value: PHONE_DISPLAY,
    action: 'Call now',
    href: PHONE_TEL,
    external: false,
  },
  {
    icon: EnvelopeSimple,
    label: 'Email',
    value: EMAIL,
    action: 'Send email',
    href: `mailto:${EMAIL}`,
    external: false,
  },
  {
    icon: MapPin,
    label: 'Visit',
    value: 'PetCart Nest, Sarjapura',
    action: 'Get directions',
    href: 'https://maps.google.com/?q=PetCart+Nest+Sarjapura+Bangalore',
    external: true,
  },
];

const hours = [
  { day: 'Monday – Saturday', time: '8:00 AM – 8:00 PM' },
  { day: 'Sunday', time: '9:00 AM – 6:00 PM' },
];

const reasons = [
  { label: 'Boarding enquiry', value: 'Boarding' },
  { label: 'Daycare or walking', value: 'Daycare/walking' },
  { label: 'Grooming', value: 'Grooming' },
  { label: 'Wag & Wine reservation', value: 'Wag & Wine' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Other', value: 'Other' },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: reasons[0].value,
    message: '',
  });
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const result = await submitLead({
      source: 'contact',
      name: form.name.trim(),
      phone: normalizePhone(form.phone),
      email: form.email.trim(),
      reason: form.reason,
      message: form.message.trim(),
      consent,
    });
    setSubmitting(false);
    if (result?.success) setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />

      {/* Hero */}
      <section className="relative h-[380px] min-h-[340px] overflow-hidden bg-neutral-950 pt-14 sm:pt-16 lg:h-[56vh] lg:min-h-[440px]">
        <img
          src="https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?auto=format&fit=crop&w=2000&q=80"
          alt="Get in touch with PetCart"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/75" />
        <div className="absolute inset-0 z-10 mx-auto flex max-w-4xl flex-col items-center justify-center px-5 pt-[140px] text-center text-white sm:pt-[150px] lg:pt-[160px]">
          <div className="landing-hero-eyebrow">Contact</div>
          <h1 className="landing-hero-title mt-1">We'd love to hear from you</h1>
          <p className="landing-hero-lede mt-4">
            Questions, bookings, or just saying hi — pick the channel that works for you and we'll get back fast.
          </p>
        </div>
      </section>

      {/* Channels */}
      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="landing-section-header">
            <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
              Reach us
            </span>
            <h2 className="landing-section-title">Pick your channel</h2>
            <p className="landing-section-lede mx-auto">
              WhatsApp is fastest. Calls and email are answered within business hours.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.external ? '_blank' : undefined}
                  rel={c.external ? 'noopener noreferrer' : undefined}
                  className="group flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition-colors hover:border-primary hover:bg-primary-light"
                >
                  <Icon size={26} weight="duotone" className="mb-4 text-primary" />
                  <p className="landing-card-label">{c.label}</p>
                  <p className="landing-card-meta mt-1">{c.value}</p>
                  <span className="mt-4 text-xs font-semibold uppercase tracking-wide text-primary">
                    {c.action} →
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="bg-neutral-100 py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
              <div>
                <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
                  Send a message
                </span>
                <h2 className="mt-3 text-2xl font-semibold text-neutral-900 sm:text-3xl">
                  Drop us a line
                </h2>
                <p className="mt-2 text-sm text-neutral-600">
                  Fill this in and we'll reply by email or phone.
                </p>
              </div>

              {submitted ? (
                <div className="mt-8 rounded-xl border border-primary/30 bg-primary-light p-6 text-sm text-neutral-800">
                  Thanks — we've received your message and the team will reach out shortly. For anything urgent, write to <a className="font-semibold text-primary" href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      maxLength={20}
                      value={form.phone}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={254}
                      value={form.email}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="reason" className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Reason
                    </label>
                    <select
                      id="reason"
                      name="reason"
                      value={form.reason}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      {reasons.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      maxLength={2000}
                      value={form.message}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FormPrivacyNotice checked={consent} onChange={setConsent} id="contact-privacy-consent" />
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-neutral-500">
                      Prefer chat? <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary">Message on WhatsApp</a>.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <PaperPlaneTilt size={16} weight="bold" />
                      {submitting ? 'Sending…' : 'Send message'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-neutral-200 bg-white p-6">
                <Clock size={22} weight="duotone" className="mb-3 text-primary" />
                <p className="landing-card-label">Hours</p>
                <ul className="mt-3 space-y-2">
                  {hours.map((h) => (
                    <li key={h.day} className="flex justify-between text-sm">
                      <span className="text-neutral-600">{h.day}</span>
                      <span className="font-medium text-neutral-900">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white p-6">
                <MapPin size={22} weight="duotone" className="mb-3 text-primary" />
                <p className="landing-card-label">PetCart Nest</p>
                <p className="mt-2 text-sm text-neutral-600">{ADDRESS}</p>
                <a
                  href="https://maps.google.com/?q=PetCart+Nest+Sarjapura+Bangalore"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-block text-xs font-semibold uppercase tracking-wide text-primary"
                >
                  Open in Maps →
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
